// External libraries
import { TextButton } from '@checkout/react-components';
import { bool } from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';

// Internal modules
import {
  ERROR_COLOR_V3,
  HIGH_CONTRAST_TEXT_COLOR,
  LABEL_COLOR,
  PRIMARY_COLOR_V2,
  TEXT_COLOR_V2,
  WHITE_COLOR,
  YELLOW_BACKGROUND_COLOR,
} from '@/styles';
import { features, getLocale } from '@/lib';
import {
  getCommonRadioButtonStyleOverrides,
  getLinkStateStyles,
} from '@/components/Common';
import { CountryCodes } from '@/generated/graphql';

const getChevronDirection = ({ isOpen }) => `rotate(${isOpen ? -45 : 135}deg)`;

// The vertical position of the chevron depends on its direction (up/down)
const getVerticalOffset = ({ isOpen }) => {
  const divisor = isOpen ? 4 : 2;

  // Why `Math.sqrt(98)`? The chevron is a square with a side of 7px and a
  // border on the top and right sides. It is then rotated to point up or down.
  // The "height" of the rotated square is sqrt(7^2 + 7^2) === sqrt(98)
  return `translateY(-${Math.sqrt(98) / divisor}px)`;
};

const HonorFxButton = styled(TextButton)`
  line-height: 1.38;
  margin-top: 6px;
  padding-right: 17px;
  position: relative;
  ${({ theme }) => getLinkStateStyles(theme)}

  /* Angle down/up chevron */
  &::after {
    border-style: solid;
    border-width: 2px 2px 0 0;
    content: '';
    height: 7px;
    position: absolute;
    right: 2px;
    top: 50%;
    transform: ${getVerticalOffset} ${getChevronDirection};
    width: 7px;
  }
`;

HonorFxButton.propTypes = {
  isOpen: bool.isRequired,
};

const HonorFxContent = styled.p`
  margin-top: 6px;
`;

const getStyleOverrides = () => {
  return {
    paypalDisclosure: css`
      li {
        font-size: 18px;
        font-weight: 400;
      }

      p {
        font-size: 14px;
        color: ${LABEL_COLOR};
        line-height: 20px;

        span {
          font-size: 18px;
          font-weight: 400;
        }
      }

      button {
        color: ${PRIMARY_COLOR_V2};
        font-size: 14px;
        text-decoration: none;
      }
    `,
    radioButton: {
      // displayError set to false, currency conversion sets by default
      ...getCommonRadioButtonStyleOverrides({ displayError: false }),
    },
    totalAmount: css`
      dt,
      dd {
        color: ${HIGH_CONTRAST_TEXT_COLOR};
        font-weight: 400;
        font-size: 18px;
      }
    `,
    vendorDisclosure: css`
      p {
        color: ${ERROR_COLOR_V3};
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        max-width: none;
      }

      svg {
        margin-top: 1px;
        margin-left: 2px;
        width: 52px;
      }

      svg path {
        fill: ${WHITE_COLOR};
      }

      svg circle {
        fill: ${ERROR_COLOR_V3};
      }
    `,
  };
};

const getVendorDisclosureStyle = () => {
  const isUnitedStates = getLocale().country === CountryCodes.Us;
  return isUnitedStates
    ? {
        vendorDisclosure: css`
          p {
            color: ${TEXT_COLOR_V2};
          }

          svg path {
            fill: ${TEXT_COLOR_V2};
          }

          svg circle {
            stroke: ${TEXT_COLOR_V2};
          }
        `,
      }
    : {};
};

const vendorDisclosureYellowStyle = () => {
  const isCurrencyConversionInfoMsgStyleActive = features.isActive(
    'isCurrencyConversionInfoMsgStyleActive',
  );
  return isCurrencyConversionInfoMsgStyleActive
    ? {
        vendorDisclosure: css`
          p {
            color: ${HIGH_CONTRAST_TEXT_COLOR};
            background-color: ${YELLOW_BACKGROUND_COLOR};
            border-radius: 6px;
            padding: 12px 16px;
            font-size: 14px;
            font-weight: 400;
            line-height: 20px;
            max-width: none;
            margin-left: 0;
          }

          svg {
            display: none;
          }
        `,
      }
    : {};
};

export {
  HonorFxButton,
  HonorFxContent,
  getStyleOverrides,
  getVendorDisclosureStyle,
  vendorDisclosureYellowStyle,
};
